<script setup lang="ts">
  import { computed } from 'vue'
  const props = defineProps({
    currentPage: {
      type: Number,
      required: true,
    },
    totalCount: {
      type: Number,
      required: true,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    maxPagination: {
      type: Number,
      default: 10,
    },
  })

  const emits = defineEmits(['update:currentPage'])

  const isFirstPage = computed(() => {
    return props.currentPage === 1
  })

  const isLastPage = computed(() => {
    return props.currentPage === totalPageCount.value
  })

  const totalPageCount = computed(() => {
    return Math.ceil(props.totalCount / props.itemsPerPage)
  })

  const firstPageInPagination = computed(() => {
    const index = props.currentPage - 1
    return index - (index % props.maxPagination) + 1
  })

  const currentPageList = computed(() => {
    const remainedLength = Math.min(totalPageCount.value - firstPageInPagination.value + 1, props.maxPagination)
    return Array.from({ length: remainedLength }, (_, i) => i + firstPageInPagination.value)
  })

  const nextPage = () => {
    if (props.currentPage !== totalPageCount.value) {
      goToPage(props.currentPage + 1)
    }
  }

  const backPage = () => {
    if (props.currentPage !== 1) {
      goToPage(props.currentPage - 1)
    }
  }

  const goToPage = (numPage: number) => {
    emits('update:currentPage', numPage)
  }
  defineExpose({
    totalPageCount,
    currentPageList,
    goToPage,
    nextPage,
    backPage,
  })
</script>
<template>
  <div class="pagination__ui-wrapper">
    <div class="pagination__ui-inner-wrapper font ml">
      <div class="pagination__go-first-page" :class="{ inactive: isFirstPage }" @click="goToPage(1)"></div>
      <div class="pagination__go-prev-page" :class="{ inactive: isFirstPage }" @click="backPage()"></div>
      <div v-if="currentPage > props.maxPagination">...</div>
      <div class="pagination__numbers">
        <div
          v-for="page in currentPageList"
          :key="page"
          class="pagination__number"
          :class="{ current: currentPage == page }"
          @click="() => goToPage(page)"
        >
          <span class="pagination__number-text">{{ page }}</span>
        </div>
      </div>
      <div v-if="totalPageCount - firstPageInPagination + 1 > props.maxPagination">...</div>
      <div class="pagination__go-next-page" :class="{ inactive: isLastPage }" @click="nextPage()"></div>
      <div class="pagination__go-last-page" :class="{ inactive: isLastPage }" @click="goToPage(totalPageCount)"></div>
    </div>
  </div>
</template>
<style scoped lang="scss">
  .pagination__ui-wrapper {
    padding-top: 20px;
    display: flex;
    justify-content: center;
    font-size: 18px;
  }
  .pagination__ui-inner-wrapper {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    line-height: 1.75rem;
  }
  .pagination__numbers {
    margin-top: 3.5px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  .pagination__number {
    position: relative;
    cursor: pointer;
    transition: color 0.15s;
    margin: 0 10px;

    &:hover {
      font-weight: bold;
      color: var(--accent-news-color1);
    }

    .pagination__number-text {
      display: inline-block;
      margin-bottom: 0 !important;
      position: relative;
      z-index: 1;
      transition: all 0.2s;
    }

    &:before {
      position: absolute;
      content: '';
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 40px;
      height: 40px;
      z-index: 0;
      transition: opacity 0.3s;
      opacity: 0;
    }
    &.current {
      .pagination__number-text {
        font-weight: bold;
        color: var(--accent-news-color1);
      }

      &:before {
        opacity: 1;
      }
    }
  }

  %pagination__icon {
    cursor: pointer;
    width: 30px;
    height: 15px;
    background: no-repeat center center / auto 100%;
    transition: background 0.15s, opacity 0.15s;
    &:after {
      content: '';
      opacity: 0;
    }

    &.inactive {
      opacity: 0.5;
      filter: grayscale(100%);
    }
  }
  .pagination__go-first-page {
    @extend %pagination__icon;
    background-image: url('@/assets/imgs/components/pagination/go-to-first-icon/default.png');
    &:after,
    &:hover:not(&.inactive) {
      background-image: url('@/assets/imgs/components/pagination/go-to-first-icon/hover.png');
    }
  }

  .pagination__go-prev-page {
    @extend %pagination__icon;
    background-image: url('@/assets/imgs/components/pagination/go-to-prev-icon/default.png');
    &:after,
    &:hover:not(&.inactive) {
      background-image: url('@/assets/imgs/components/pagination/go-to-prev-icon/hover.png');
    }
  }
  .pagination__go-next-page {
    @extend %pagination__icon;
    background-image: url('@/assets/imgs/components/pagination/go-to-next-icon/default.png');
    &:after,
    &:hover:not(&.inactive) {
      background-image: url('@/assets/imgs/components/pagination/go-to-next-icon/hover.png');
    }
  }

  .pagination__go-last-page {
    @extend %pagination__icon;
    background-image: url('@/assets/imgs/components/pagination/go-to-last-icon/default.png');
    &:after,
    &:hover:not(&.inactive) {
      background-image: url('@/assets/imgs/components/pagination/go-to-last-icon/hover.png');
    }
  }
</style>
